/* eslint-disable i18next/no-literal-string */

import {
  ChatContainer,
  ConversationHeader,
  MainContainer,
  Message,
  MessageInput,
  MessageList,
} from '@chatscope/chat-ui-kit-react';
import { Alert, Button } from 'antd';
import React, { useEffect, useRef, useState } from 'react';

import './advisorChatPopper.scss';
import {
  CheckOutlined,
  CloseOutlined,
  CopyOutlined,
  EditOutlined,
  ExpandAltOutlined,
  FileTextOutlined,
  MessageOutlined,
  QuestionCircleOutlined,
} from '@ant-design/icons';
import { Trans, useTranslation } from 'react-i18next';
import { AdvisorChatSubjectTypeEnum } from '../api/generated/models/advisor-chat-subject-type-enum';
import { useStore } from '../context';
import { type ChatMessage, useAdvisorChat } from '../services/AdvisorChatService';
import { formatAdvisorChatMessageHtml, messageContentToPlainTextForCopy } from './advisorChatMarkdown';

export const AdvisorChatPopper = () => {
  const { clinician } = useStore();
  const { t } = useTranslation();

  const searchParams = new URLSearchParams(location.search);
  const startQuery = searchParams.get('start');
  const endQuery = searchParams.get('end');

  const [showChat, setShowChat] = useState(false);
  const [showDisclaimer, setShowDisclaimer] = useState(false);
  const [sessionId, setSessionId] = useState<number | null>(null);
  const [dateChanged, setDateChanged] = useState(false); // used for if the user changes the date while advisor is loading
  const lastMessageRef = useRef<HTMLDivElement>(null);

  const [isExpanded, setIsExpanded] = useState(false);
  const [copiedMessageId, setCopiedMessageId] = useState<number | null>(null);

  const advisorChatQuery = useAdvisorChat({
    sessionId: sessionId,
    subjectType: AdvisorChatSubjectTypeEnum.General,
  });

  const scrollToBottom = () => {
    requestAnimationFrame(() => {
      if (lastMessageRef.current) {
        const scrollContainer = lastMessageRef.current.closest('.scrollbar-container');
        if (scrollContainer) {
          scrollContainer.scrollTop = scrollContainer.scrollHeight;
        }
      }
    });
  };

  const handleMessageSend = async (message: string) => {
    scrollToBottom();
    const res = await advisorChatQuery.sendMessage(message);
    if (!sessionId && !dateChanged) {
      setSessionId(res.sessionId);
    }
    setDateChanged(false);
  };

  const mostRecentMessage = advisorChatQuery.messages?.[0];
  const shouldShowTyping =
    (!advisorChatQuery.sendMessageMutation.isIdle && advisorChatQuery.sendMessageMutation.isLoading)
    && (!mostRecentMessage || mostRecentMessage.role !== 'assistant');

  useEffect(() => {
    scrollToBottom();
  }, [advisorChatQuery.messages, shouldShowTyping]);

  useEffect(() => {
    setSessionId(null);
    advisorChatQuery.setLlmContext({
      filterDatesLte: endQuery,
      filterDatesGte: startQuery,
    });
    if (advisorChatQuery.sendMessageMutation.isLoading) {
      setDateChanged(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startQuery, endQuery]);

  const popperHeight = isExpanded ? '90vh' : '85vh';
  const popperWidth = isExpanded ? '50%' : '20%';

  const handleCopyToClipboard = (id: number, message: ChatMessage) => {
    const contentInText = messageContentToPlainTextForCopy(message);
    navigator.clipboard.writeText(contentInText);
    // change clipboard icon to check mark for 3 seconds
    setCopiedMessageId(id);
    setTimeout(() => {
      setCopiedMessageId(null);
    }, 3000);
  };

  if (!clinician.flags?.clinic_enable_clinician_advisor_chat) {
    return null;
  }

  const iconHeight = '150px';

  return (
    <>
      {showDisclaimer && <DisclaimerPopup setShowDisclaimer={setShowDisclaimer} />}
      <div
        className="rx-popper-container"
        style={{
          height: popperHeight,
          width: popperWidth,
        }}
      >
        <div
          className={`rx-chat-panel ${showChat ? 'rx-chat-panel--open' : ''}`}
          style={{
            top: '0',
            bottom: iconHeight,
            position: 'absolute',
            width: '100%',
            paddingBottom: '10px',
          }}
        >
          <MainContainer>
            <ChatContainer>
              <ConversationHeader>
                <ConversationHeader.Content userName="RxAssistant" />
                <ConversationHeader.Actions>
                  <ExpandAltOutlined
                    translate="no"
                    rev="0"
                    style={{
                      fontSize: 18,
                      color: 'white',
                      cursor: 'pointer',
                      opacity: 0.85,
                      transition: 'opacity 0.2s',
                    }}
                    onClick={() => setIsExpanded(!isExpanded)}
                  />
                  <CloseOutlined
                    translate="no"
                    rev="0"
                    style={{
                      fontSize: 18,
                      color: 'white',
                      marginLeft: '12px',
                      cursor: 'pointer',
                      opacity: 0.85,
                      transition: 'opacity 0.2s',
                    }}
                    onClick={() => setShowChat(false)}
                  />
                </ConversationHeader.Actions>
              </ConversationHeader>
              <MessageList scrollBehavior="smooth">
                {(!advisorChatQuery.messages || advisorChatQuery.messages?.length === 0)
                  ? (
                    <WelcomeState
                      onSuggestionClick={handleMessageSend}
                      onDisclaimerClick={() => setShowDisclaimer(true)}
                    />
                  )
                  : (
                    <>
                      <div className="rx-disclaimer-banner">
                        <Trans>
                          RxAssistant can make mistakes. Review the{' '}
                          <a onClick={() => setShowDisclaimer(true)} className="rx-disclaimer-link">
                            disclaimer
                          </a>{' '}
                          before continuing.
                        </Trans>
                      </div>
                      {[...(advisorChatQuery.messages ?? [])].reverse().map(message => (
                        <Message
                          key={message.id}
                          model={{
                            message: formatAdvisorChatMessageHtml(message),
                            sentTime: message.timestamp,
                            sender: message.role,
                            direction: message.role === 'user' ? 'outgoing' : 'incoming',
                            position: 'single',
                            type: 'html',
                          }}
                        >
                          {message.role === 'assistant' && (
                            <Message.Footer>
                              <span className="rx-copy-btn">
                                {copiedMessageId === message.id
                                  ? (
                                    <span className="rx-copy-btn__check">
                                      <CheckOutlined translate="no" rev="0" style={{ fontSize: 14 }} />
                                      <span className="rx-copy-btn__label">{t('Copied')}</span>
                                    </span>
                                  )
                                  : (
                                    <span
                                      className="rx-copy-btn__icon"
                                      onClick={() => handleCopyToClipboard(message.id, message)}
                                    >
                                      <CopyOutlined translate="no" rev="0" style={{ fontSize: 14 }} />
                                      <span className="rx-copy-btn__label">{t('Copy')}</span>
                                    </span>
                                  )}
                              </span>
                            </Message.Footer>
                          )}
                        </Message>
                      ))}
                    </>
                  )}
                {shouldShowTyping && (
                  <div className="rx-typing-indicator">
                    <div className="rx-typing-indicator__dots">
                      <div className="rx-typing-indicator__dot" />
                      <div className="rx-typing-indicator__dot" />
                      <div className="rx-typing-indicator__dot" />
                    </div>
                    <div className="rx-typing-indicator__text">
                      <Trans>RxAssistant is thinking...</Trans>
                    </div>
                  </div>
                )}
                {advisorChatQuery.sendError && !shouldShowTyping && (
                  <Alert
                    type="error"
                    showIcon
                    closable
                    onClose={advisorChatQuery.clearSendError}
                    message={t("Couldn't send your message")}
                    description={t('Please try again. If this keeps happening, contact support.')}
                    action={(
                      <Button
                        size="small"
                        type="primary"
                        danger
                        onClick={() => {
                          advisorChatQuery.retryLastMessage();
                        }}
                      >
                        {t('Retry')}
                      </Button>
                    )}
                    style={{ margin: '8px 12px' }}
                  />
                )}
                <div ref={lastMessageRef} style={{ marginBottom: '10px' }} />
              </MessageList>
              <MessageInput
                attachButton={false}
                placeholder={t('Type message here')}
                disabled={advisorChatQuery.sendMessageMutation.isLoading}
                onSend={handleMessageSend}
                onPaste={e => {
                  e.preventDefault();
                  const text = e.clipboardData.getData('text/plain');
                  document.execCommand('insertText', false, text);
                }}
              />
            </ChatContainer>
          </MainContainer>
        </div>
        <div
          style={{
            position: 'absolute',
            bottom: '0',
            height: iconHeight,
            width: '100%',
            textAlign: 'right',
            pointerEvents: 'auto',
          }}
        >
          <img
            src="/logoRxAssistant.png"
            alt="RxAssistant"
            style={{ width: 'auto', height: '150px' }}
            onClick={() => setShowChat(!showChat)}
          />
        </div>
      </div>
    </>
  );
};

const WelcomeState = (props: {
  onSuggestionClick: (question: string) => Promise<void>,
  onDisclaimerClick: () => void,
}) => {
  const { t } = useTranslation();

  return (
    <div className="rx-welcome">
      <div className="rx-welcome__icon">
        <MessageOutlined translate="no" rev="0" />
      </div>
      <div className="rx-welcome__title">
        <Trans>How can I help you today?</Trans>
      </div>
      <div className="rx-welcome__subtitle">
        <Trans>
          Ask me anything about your patient's data, or try one of these:
        </Trans>
      </div>
      <QuestionSuggestions onClick={props.onSuggestionClick} />
      <div className="rx-disclaimer-banner" style={{ marginTop: '16px' }}>
        <Trans>
          RxAssistant can make mistakes. Review the{' '}
          <a onClick={props.onDisclaimerClick} className="rx-disclaimer-link">
            disclaimer
          </a>{' '}
          before continuing.
        </Trans>
      </div>
    </div>
  );
};

const SUGGESTION_ICONS: Record<number, React.ReactNode> = {
  0: <EditOutlined translate="no" rev="0" />,
  1: <FileTextOutlined translate="no" rev="0" />,
  2: <QuestionCircleOutlined translate="no" rev="0" />,
  3: <QuestionCircleOutlined translate="no" rev="0" />,
  4: <MessageOutlined translate="no" rev="0" />,
};

const QuestionSuggestions = (props: { onClick: (question: string) => Promise<void> }) => {
  const { t } = useTranslation();

  const INITIAL_QUESTIONS = [
    t('Write a progress note for me'),
    t('Create a meal plan'),
    t('What are high protein foods this person ate, and how can we increase it?'),
    t('What are high fibre foods this person ate and how can we increase them?'),
    t('What are three suggestions I can provide this patient based on their data?'),
  ];

  return (
    <div className="rx-suggestions">
      {INITIAL_QUESTIONS.map((question, index) => (
        <div
          key={question}
          className="rx-suggestion-card"
          onClick={() => props.onClick(question)}
        >
          <span className="rx-suggestion-card__icon">{SUGGESTION_ICONS[index]}</span>
          <span className="rx-suggestion-card__text">{question}</span>
        </div>
      ))}
    </div>
  );
};

// <strong> tags don't work, this is the workaround
const Strong = ({ children }: { children: React.ReactNode }) => {
  return <span style={{ fontWeight: 'bold' }}>{children}</span>;
};

// If only one <Trans> tag is used, the translation does not work
const DisclaimerPopup = ({ setShowDisclaimer }: { setShowDisclaimer: (show: boolean) => void }) => {
  return (
    <div id="overlay">
      <div id="popper">
        <Trans>
          <p className="disclaimer-p">
            <Strong>Important Notice:</Strong>{' '}
            Please read this disclaimer carefully before using the RxAssistant within the RxFood Clinician Portal
          </p>
          <p className="disclaimer-p">
            The RxAssistant (e.g., "AI Clinical Assistant," "Automated Documentation Aid," "Smart Suggestion Engine") is
            an artificial intelligence-powered tool designed to <Strong>assist healthcare professionals</Strong>{' '}
            in functions such as documentation, providing quick access to relevant patient data, generating meal plans,
            etc.
          </p>
          <p className="disclaimer-p">
            <Strong>Key Limitations and User Responsibilities:</Strong>
          </p>
        </Trans>
        <ol>
          <Trans>
            <li>
              <Strong>Assistance Tool Only:</Strong> The RxAssistant is an{' '}
              <Strong>
                assistance tool and not a substitute for professional medical judgment, clinical expertise, or direct
                patient care.
              </Strong>{' '}
              All information, suggestions, or outputs generated by this agent are for informational and facilitative
              purposes only.
            </li>
            <li>
              <Strong>No Diagnostic or Treatment Authority:</Strong> This agent{' '}
              <Strong>does not diagnose, treat, or provide medical advice.</Strong>{' '}
              Clinical decisions, diagnoses, treatment plans, and patient management remain the sole responsibility of
              the qualified healthcare professional.
            </li>
            <li>
              <Strong>Accuracy and Verification:</Strong>{' '}
              While efforts are made to ensure the accuracy and relevance of the information processed by the agent,
              {' '}
              <Strong>
                its outputs may contain errors, omissions, or inaccuracies.
              </Strong>{' '}
              Healthcare professionals are
              <Strong>
                solely responsible for verifying the accuracy, completeness, and appropriateness of all information
              </Strong>{' '}
              generated by or presented through this agent before relying on it or incorporating it into patient records
              or care.
            </li>
            <li>
              <Strong>Data Source Limitations:</Strong>{' '}
              The agent's outputs are based on the data available within RxFood clinician portal and its underlying
              algorithms. It may not account for all relevant clinical context, new information not yet entered, or
              nuances of individual patient cases.
            </li>
            <li>
              <Strong>Continuous Learning and Evolution:</Strong>{' '}
              AI models may evolve over time. The performance and outputs of the RxAssistant may change as it learns and
              is updated.
            </li>
            <li>
              <Strong>User Accountability:</Strong>{' '}
              By using the RxAssistant, you acknowledge and agree that you are ultimately responsible for all patient
              care decisions, documentation, and compliance with all applicable laws, regulations, and professional
              standards.
            </li>
          </Trans>
        </ol>
        <br />
        <Trans>
          <p className="disclaimer-p">
            <Strong>
              By proceeding to use the RxAssistant, you acknowledge that you have read, understood, and agree to the
              terms of this disclaimer.
            </Strong>
          </p>
        </Trans>
        <div style={{ textAlign: 'center' }}>
          <button
            className="disclaimer-button"
            onClick={() => setShowDisclaimer(false)}
          >
            <Trans>I understand</Trans>
          </button>
        </div>
      </div>
    </div>
  );
};
